package one.healthium.today.ui.carni

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ifaandroid.feature.today_v2.today_ui_carni.generated.resources.Res
import ifaandroid.feature.today_v2.today_ui_carni.generated.resources.ic_plus_20
import loco.GENERAL_OPEN
import loco.GENERAL_STEPS
import loco.TODAY_LOGGED_SYMPTOMS_LABEL
import loco.TODAY_LOG_CONTROLS_SECTION
import loco.TODAY_LOG_SYMPTOMS_LABEL
import loco.TODAY_LOG_WATER_BUTTON
import loco.TODAY_LOG_WEIGHT_BUTTON
import loco.TODAY_MEAL_PLAN_SECTION
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.relativeDate
import one.healthium.base.utils.toVolumeString
import one.healthium.base.utils.toVolumeUnit
import one.healthium.healthKit.ui.HealthKitConnectBanner
import one.healthium.healthKit.ui.HealthKitConnectBannerContract
import one.healthium.loco.resolve
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.rateus.data.ShowAppRateForStart
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.TodayViewModel
import one.healthium.today.ui.carni.components.BaseLogControl
import one.healthium.today.ui.carni.components.Header
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black30
import one.healthium.ui.compose.theme.black_4
import one.healthium.ui.compose.theme.blue_4
import one.healthium.ui.compose.theme.green_14
import one.healthium.ui.compose.theme.green_6
import one.healthium.ui.compose.theme.pink_2
import one.healthium.ui.compose.theme.yellow_4
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel


interface TodayEventDelegate {
    fun onMealPlanClicked(id: String)
    fun onLogFoodClicked()
    fun onLogWaterClicked()
    fun onLogWeightClicked()
    fun onLogSymptomsClicked()
}

@Composable
fun TodayScreen(
    eventDelegate: TodayEventDelegate
) {
    PlatformColorsEffect(lightStatusBar = true)

    val viewModel = koinViewModel<TodayViewModel>()
    val state by viewModel.state().collectAsState()

    ShowAppRateForStart()

    TodayScreenContent(
        state = state,
        onLogWaterClicked = eventDelegate::onLogWaterClicked,
        onLogWeightClicked = eventDelegate::onLogWeightClicked,
        onMealPlanClicked = { eventDelegate.onMealPlanClicked(it.id) },
        onAddFoodClicked = eventDelegate::onLogFoodClicked,
        onLogSymptomsClicked = eventDelegate::onLogSymptomsClicked,
        healthKitContract = viewModel,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
internal fun TodayScreenContent(
    state: TodayUiState,
    onLogWaterClicked: () -> Unit,
    onLogWeightClicked: () -> Unit,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
    onAddFoodClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
    healthKitContract: HealthKitConnectBannerContract,
) {
    AppScaffold(Modifier.testTag("today_screen")) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
        ) {
            Header(
                state = state,
                onAddFoodClicked = onAddFoodClicked,
                onLogSymptomsClicked = onLogSymptomsClicked,
            )
            if (state.shouldShowHKBanner) {
                Spacer(Modifier.height(24.dp))
                HealthKitConnectBanner(contract = healthKitContract)
                Spacer(Modifier.height(12.dp))
            } else {
                Spacer(Modifier.height(12.dp))
            }
            Section(stringResource(loco.Res.string.TODAY_LOG_CONTROLS_SECTION))
            LogWeightControl(state, onLogWeightClicked)
            Spacer(Modifier.height(12.dp))
            LogWaterControl(state, onLogWaterClicked)
            Spacer(Modifier.height(12.dp))
            LogSymptomsControl(state, onLogSymptomsClicked)
            state.todayStepsCounter?.let {
                Spacer(Modifier.height(12.dp))
                StepsCounterBanner(it)
            }
            Spacer(Modifier.height(12.dp))
            Section(stringResource(loco.Res.string.TODAY_MEAL_PLAN_SECTION))
            MealPlanControl(state, onMealPlanClicked)
            Spacer(Modifier.height(90.dp))
        }
    }
}

@Composable
private fun Section(text: String) {
    Text(
        modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
        text = text,
        style = AppTheme.textStyles.secondary_17_500,
        color = black_4,
    )
}

@Composable
private fun LogWaterControl(state: TodayUiState, onLogWaterClicked: () -> Unit) {
    LogControl(
        modifier = Modifier.padding(horizontal = 12.dp),
        containerColor = blue_4,
        icon = AppIcons.icBottle,
        contentPrimary = "${state.todayWaterIntake?.currentValue?.toVolumeString(state.isMetric)}" +
                " / " +
                "${state.todayWaterIntake?.dailyAmount?.toVolumeString(state.isMetric)}",
        contentPrimaryModifier = Modifier.testTag("water_daily_intake"),
        contentSecondary = " ${state.isMetric.toVolumeUnit().resolve()}",
        button = stringResource(loco.Res.string.TODAY_LOG_WATER_BUTTON),
        buttonModifier = Modifier.testTag("log_water"),
        onClick = onLogWaterClicked,
    )
}

@Composable
private fun LogWeightControl(
    state: TodayUiState,
    onLogWeightClicked: () -> Unit
) {
    var weightValue: String? = null
    var dateValue: String? = null

    val latestWeightLog = state.latestWeightLog
    if (latestWeightLog != null) {
        weightValue = latestWeightLog.currentWeight.formatWeight(state.isMetric).resolve()
        dateValue = latestWeightLog.date?.relativeDate()?.resolve().orEmpty()
    }

    BaseLogControl(
        modifier = Modifier.padding(horizontal = 12.dp),
        containerColor = pink_2,
        icon = AppIcons.icFileChecked,
        button = stringResource(loco.Res.string.TODAY_LOG_WEIGHT_BUTTON),
        buttonModifier = Modifier.testTag("log_weight"),
        onClick = onLogWeightClicked,
    ) {
        Column {
            if (!weightValue.isNullOrBlank()) {
                Text(
                    modifier = Modifier.testTag("weight_value"),
                    text = weightValue,
                    style = AppTheme.textStyles.secondary_17_500,
                    color = AppTheme.colors.text100,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            if (!dateValue.isNullOrBlank()) {
                Text(
                    text = dateValue,
                    style = AppTheme.textStyles.secondary_17_400,
                    color = black30,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
fun StepsCounterBanner(
    steps: DailyStepsUiModel
) {
    LogControl(
        modifier = Modifier
            .padding(horizontal = 12.dp)
            .height(70.dp),
        containerColor = green_6,
        icon = AppIcons.icSteps,
        contentPrimary = "${steps.steps} / ${steps.goal}",
        contentSecondary = " ${stringResource(loco.Res.string.GENERAL_STEPS)}",
    )
}

@Composable
private fun MealPlanControl(state: TodayUiState, onClick: (MealPlanInfoModel) -> Unit) {
    val mealPlan = state.mealPlan

    BaseLogControl(
        modifier = Modifier.padding(horizontal = 12.dp),
        containerColor = yellow_4,
        icon = AppIcons.icPizza,
        button = stringResource(loco.Res.string.GENERAL_OPEN),
        onClick = {
            if (mealPlan != null) {
                onClick(mealPlan)
            }
        },
    ) {
        Text(
            modifier = Modifier.testTag("current_meal_plan"),
            text = mealPlan?.name?.resolve().orEmpty(),
            style = AppTheme.textStyles.secondary_17_500.copy(lineHeight = 20.sp),
            color = AppTheme.colors.text100,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
private fun LogSymptomsControl(state: TodayUiState, onClick: () -> Unit) {
    LogControl(
        modifier = Modifier.padding(horizontal = 12.dp),
        containerColor = green_14,
        icon = Res.drawable.ic_plus_20,
        contentPrimary = state.todayLoggedSymptoms.toString(),
        contentSecondary = " ${stringResource(loco.Res.string.TODAY_LOGGED_SYMPTOMS_LABEL)}",
        button = stringResource(loco.Res.string.TODAY_LOG_SYMPTOMS_LABEL),
        buttonModifier = Modifier.testTag("log_symptoms"),
        onClick = onClick,
    )
}

@Composable
private fun LogControl(
    modifier: Modifier = Modifier,
    containerColor: Color,
    icon: DrawableResource,
    contentPrimary: String,
    contentPrimaryModifier: Modifier = Modifier,
    contentSecondary: String,
    button: String? = null,
    buttonModifier: Modifier = Modifier,
    onClick: () -> Unit = {},
) {
    BaseLogControl(
        modifier = modifier,
        containerColor = containerColor,
        icon = icon,
        button = button,
        buttonModifier = buttonModifier,
        onClick = onClick,
    ) {
        Row {
            Text(
                modifier = contentPrimaryModifier,
                text = contentPrimary,
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.text100,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = contentSecondary,
                style = AppTheme.textStyles.secondary_17_400,
                color = black30,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

